import { useAuth } from '../components/auth/AuthProvider';

// API client utility for making authenticated requests to the backend
export class ApiClient {
  private baseUrl: string;
  private acquireApiToken: () => Promise<string | null>;

  constructor(baseUrl: string, acquireApiToken: () => Promise<string | null>) {
    this.baseUrl = baseUrl;
    this.acquireApiToken = acquireApiToken;
  }

  private async getAuthHeaders(): Promise<Record<string, string>> {
    const token = await this.acquireApiToken();
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return headers;
  }

  async get<T>(endpoint: string): Promise<T> {
    const headers = await this.getAuthHeaders();
    // Apply ETag caching only for /auth/profile
    const isProfile = endpoint.replace(/\?.*$/, '') === '/auth/profile';
    const etagKey = 'profile_etag';
    const cacheKey = 'last_known_profile';
    if (isProfile) {
      try {
        const etag = window.localStorage.getItem(etagKey);
        if (etag) (headers as any)['If-None-Match'] = etag;
      } catch {}
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'GET',
      headers,
    });

    if (isProfile && response.status === 304) {
      // Not modified: return cached profile if present
      try {
        const cached = window.localStorage.getItem(cacheKey);
        if (cached) {
          const parsed = JSON.parse(cached);
          return parsed.profile as T;
        }
      } catch {}
      // Fallback: still return minimal OK
      return {} as T;
    }

    if (!response.ok) {
      let errMsg = `API request failed: ${response.status} ${response.statusText}`;
      let payload: any = undefined;
      try { payload = await response.json(); } catch {}
      if (payload?.detail) errMsg = payload.detail;
      else if (payload?.message) errMsg = payload.message;
      const err: any = new Error(errMsg);
      err.response = { status: response.status, data: payload ?? null, headers: Object.fromEntries(response.headers.entries()) };
      throw err;
    }

    const data = await response.json();
    if (isProfile) {
      try {
        const newEtag = response.headers.get('ETag');
        if (newEtag) window.localStorage.setItem(etagKey, newEtag);
        window.localStorage.setItem(cacheKey, JSON.stringify({ profile: data, ts: Date.now() }));
      } catch {}
    }
    return data;
  }

  // Multipart upload with progress and timeout using XMLHttpRequest
  async postMultipart<T>(
    endpoint: string,
    formData: FormData,
    options?: { onUploadProgress?: (evt: ProgressEvent) => void; timeoutMs?: number }
  ): Promise<T> {
    const authHeaders = await this.getAuthHeaders();
    return new Promise<T>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${this.baseUrl}${endpoint}`, true);
      // auth header
      if (authHeaders['Authorization']) {
        xhr.setRequestHeader('Authorization', authHeaders['Authorization']);
      }
      // Do NOT set Content-Type for FormData; browser will set with boundary
      if (options?.timeoutMs) xhr.timeout = options.timeoutMs;
      if (options?.onUploadProgress) xhr.upload.onprogress = options.onUploadProgress;

      xhr.onreadystatechange = () => {
        if (xhr.readyState === XMLHttpRequest.DONE) {
          const contentType = xhr.getResponseHeader('Content-Type') || '';
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              if (contentType.includes('application/json')) {
                resolve(JSON.parse(xhr.responseText));
              } else {
                // Attempt JSON parse, else return as any
                try { resolve(JSON.parse(xhr.responseText)); } catch { resolve(xhr.response as any as T); }
              }
            } catch (err) {
              reject(err);
            }
          } else {
            let errorMsg = `API request failed: ${xhr.status} ${xhr.statusText}`;
            try {
              const parsed = JSON.parse(xhr.responseText);
              if (parsed?.detail) errorMsg = parsed.detail;
              else if (parsed?.message) errorMsg = parsed.message;
            } catch {}
            const err = new Error(errorMsg) as any;
            (err.response = { status: xhr.status, data: (() => { try { return JSON.parse(xhr.responseText); } catch { return xhr.responseText; } })() });
            reject(err);
          }
        }
      };
      xhr.ontimeout = () => reject(new Error('Request timed out'));
      xhr.onerror = () => reject(new Error('Network error'));
      xhr.send(formData);
    });
  }

  async post<T>(endpoint: string, data?: any): Promise<T> {
    const authHeaders = await this.getAuthHeaders();
    const isFormData = typeof FormData !== 'undefined' && data instanceof FormData;
    const headers: HeadersInit = isFormData
      ? authHeaders // Let the browser set multipart boundary
      : { 'Content-Type': 'application/json', ...authHeaders };
    const body = isFormData ? (data as FormData) : data ? JSON.stringify(data) : undefined;

    const controller = new AbortController();
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'POST',
      headers,
      body,
      signal: controller.signal,
    });

    if (!response.ok) {
      let errMsg = `API request failed: ${response.status} ${response.statusText}`;
      let payload: any = undefined;
      try { payload = await response.json(); } catch {}
      if (payload?.detail) errMsg = payload.detail;
      else if (payload?.message) errMsg = payload.message;
      const err: any = new Error(errMsg);
      err.response = { status: response.status, data: payload ?? null, headers: Object.fromEntries(response.headers.entries()) };
      throw err;
    }

    return response.json();
  }

  async put<T>(endpoint: string, data?: any): Promise<T> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'PUT',
      headers,
      body: data ? JSON.stringify(data) : undefined,
    });

    if (!response.ok) {
      let errMsg = `API request failed: ${response.status} ${response.statusText}`;
      let payload: any = undefined;
      try { payload = await response.json(); } catch {}
      if (payload?.detail) errMsg = payload.detail;
      else if (payload?.message) errMsg = payload.message;
      const err: any = new Error(errMsg);
      err.response = { status: response.status, data: payload ?? null, headers: Object.fromEntries(response.headers.entries()) };
      throw err;
    }

    return response.json();
  }

  async delete<T>(endpoint: string): Promise<T> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'DELETE',
      headers,
    });

    if (!response.ok) {
      let errMsg = `API request failed: ${response.status} ${response.statusText}`;
      let payload: any = undefined;
      try { payload = await response.json(); } catch {}
      if (payload?.detail) errMsg = payload.detail;
      else if (payload?.message) errMsg = payload.message;
      const err: any = new Error(errMsg);
      err.response = { status: response.status, data: payload ?? null, headers: Object.fromEntries(response.headers.entries()) };
      throw err;
    }

    return response.json();
  }
}

// Custom hook to get an API client instance
export const useApiClient = () => {
  const { acquireApiToken } = useAuth();
  
  return new ApiClient(
    process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v1',
    acquireApiToken
  );
};
